import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useLockBodyScrollOnTablet } from '@as/cotopaxi';
import { useAemConfig } from '@as-react/common-components';
import { MenuSearchResultBlockVariant } from '@as-react/common-types';
import { safeRedirect } from '@as-react/common-utils';
import { ArrowLeftIcon } from '@yonderland/andes-icons';
import { ListBox, SearchFieldAlpha, useMediaQueryMatchesOnDesktop, Popover, Box, IconButton, Stack, Grid, Divider, Backdrop, } from '@yonderland/andes-react';
import { useCombobox } from 'downshift';
import { useRef, useEffect, useState, Fragment, useCallback, } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { I18N_NAMESPACES } from '../../constants/i18nConstants';
import { getOptionLabel, useNewMenuSearch } from '../../hooks/useNewMenuSearch';
import { isLastItem } from '../../util/ArrayUtil';
import { appendSearchQueryToUrl, getKeywordsSuggestionsUrl } from '../../util/SearchUtil';
import { generateTestId } from '../../util/TestUtil';
import { emitter, EmitterEvent } from '../../util/tracking';
import { buildProductClickPayload } from '../../util/TrackingUtil';
import { MenuSearchBarcodeReader } from './MenuSearchBarcodeReader';
import { MenuSearchResult } from './MenuSearchResult';
import { MenuSearchResultContent } from './MenuSearchResultContent';
import { MenuSearchResultProduct } from './MenuSearchResultProduct';
import { MenuSearchResultSuggestion } from './MenuSearchResultSuggestion';
import { MenuSearchSectionTitle } from './MenuSearchSectionTitle';
const PADDING = { xs: '5', lg: '6' };
const TEST_ID = 'list-box';
const COMBOBOX_ID = 'menu-search';
const KEYBOARD_HIGHLIGHT_CHANGE_TYPES = new Set([
    useCombobox.stateChangeTypes.InputKeyDownArrowDown,
    useCombobox.stateChangeTypes.InputKeyDownArrowUp,
    useCombobox.stateChangeTypes.InputKeyDownHome,
    useCombobox.stateChangeTypes.InputKeyDownEnd,
    useCombobox.stateChangeTypes.InputKeyDownPageUp,
    useCombobox.stateChangeTypes.InputKeyDownPageDown,
]);
const POPOVER_WIDTH = 680;
const SCREEN_EDGE_OFFSET = 24;
export const NewMenuSearch = ({ createAdobeAnalyticsSetSearchTermAction, suggestionsParentLink, brandsParentLink, categoriesParentLink, setSearchValue, searchValue, }) => {
    const { t } = useTranslation(I18N_NAMESPACES.navigation);
    const isDesktop = useMediaQueryMatchesOnDesktop();
    const searchInputElementRef = useRef(null);
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState();
    const [containerLeft, setContainerLeft] = useState(0);
    const [isSearchMenuActive, setIsSearchMenuActive] = useState(false);
    const { hasSearchResults, hasSuggestionResults, hasProductResults, hasContentResults, keywordOptions, searchesBrandOrCatOptions, productOptions, contentOptions, flatOptions, } = useNewMenuSearch({ searchTerm: searchValue, suggestionsParentLink, brandsParentLink, categoriesParentLink });
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [isExpandedLayoutActive, setIsExpandedLayoutActive] = useState(false);
    const { root, isShopLanding } = useAemConfig();
    const listerPageUrl = `${root}lister.html`;
    const idealTranslateX = containerWidth ? (containerWidth - POPOVER_WIDTH) / 2 : 0;
    const translateXValue = containerWidth && typeof window !== 'undefined'
        ? Math.min(Math.max(idealTranslateX, SCREEN_EDGE_OFFSET - containerLeft), window.innerWidth - SCREEN_EDGE_OFFSET - containerLeft - POPOVER_WIDTH)
        : idealTranslateX;
    useEffect(() => {
        if (!containerRef.current)
            return undefined;
        let debounceTimeout;
        const observer = new ResizeObserver(entries => {
            const entry = entries[0];
            if (!entry)
                return;
            clearTimeout(debounceTimeout);
            debounceTimeout = setTimeout(() => {
                setContainerWidth(entry.contentRect.width);
                setContainerLeft(entry.target.getBoundingClientRect().left);
            }, 250);
        });
        observer.observe(containerRef.current);
        return () => {
            clearTimeout(debounceTimeout);
            observer.disconnect();
        };
    }, []);
    const handleInputChange = (searchValueInput) => {
        setSearchValue(searchValueInput);
    };
    const onButtonBackClickHandler = () => {
        setIsInputFocused(false);
        setIsSearchMenuActive(false);
    };
    const inputElementSetFocus = () => { var _a; return (_a = searchInputElementRef === null || searchInputElementRef === void 0 ? void 0 : searchInputElementRef.current) === null || _a === void 0 ? void 0 : _a.focus(); };
    const onInputClearClickHandler = () => {
        setSearchValue('');
    };
    const onBarcodeScanHandler = (barcode) => {
        safeRedirect(appendSearchQueryToUrl(`${listerPageUrl}?input=scanner`, barcode));
    };
    useEffect(() => {
        if (searchValue === '' && hasSearchResults) {
            inputElementSetFocus();
        }
    }, [searchValue, hasSearchResults]);
    useLockBodyScrollOnTablet(isSearchMenuActive);
    const navigateToOption = (option) => {
        var _a;
        if (option.variant === MenuSearchResultBlockVariant.SUGGESTIONS) {
            createAdobeAnalyticsSetSearchTermAction(option.data.name.trim());
            safeRedirect(option.data.url);
        }
        else {
            if (option.variant === MenuSearchResultBlockVariant.PRODUCTS) {
                emitter.emit(EmitterEvent.PRODUCT_CLICK, buildProductClickPayload((_a = option.data.productId) !== null && _a !== void 0 ? _a : option.data.productCode));
            }
            safeRedirect(appendSearchQueryToUrl(option.data.url, searchValue));
        }
    };
    const navigateToGenericSearch = () => {
        createAdobeAnalyticsSetSearchTermAction(searchValue);
        safeRedirect(getKeywordsSuggestionsUrl(suggestionsParentLink, searchValue));
    };
    const [isKeyboardHighlight, setIsKeyboardHighlight] = useState(false);
    const { isOpen: isComboboxOpen, highlightedIndex, getInputProps, getMenuProps, getItemProps, openMenu, } = useCombobox({
        id: COMBOBOX_ID,
        items: flatOptions,
        inputValue: searchValue,
        itemToString: getOptionLabel,
        onInputValueChange: ({ inputValue }) => {
            setSearchValue(inputValue !== null && inputValue !== void 0 ? inputValue : '');
        },
        onHighlightedIndexChange: ({ type }) => {
            setIsKeyboardHighlight(KEYBOARD_HIGHLIGHT_CHANGE_TYPES.has(type));
        },
        onSelectedItemChange: ({ selectedItem }) => {
            if (!selectedItem)
                return;
            setIsSearchMenuActive(false);
            navigateToOption(selectedItem);
        },
    });
    const comboboxInputProps = getInputProps({
        id: 'search',
        refKey: 'innerRef',
        ref: searchInputElementRef,
        'aria-label': t('flyout.search.placeholder'),
        'aria-autocomplete': 'both',
        onClick: (e) => {
            e.preventDownshiftDefault = true;
            if (!isInputFocused) {
                setIsInputFocused(true);
                setIsExpandedLayoutActive(true);
            }
            if (searchValue) {
                openMenu();
            }
        },
        onBlur: (e) => {
            e.preventDownshiftDefault = true;
            if (hasSearchResults && !!searchValue) {
                return;
            }
            setIsInputFocused(false);
        },
        onKeyDown: (e) => {
            if (e.key !== 'Enter')
                return;
            e.preventDefault();
            e.preventDownshiftDefault = true;
            onButtonSearchClickHandler();
        },
    });
    const menuProps = getMenuProps({}, { suppressRefError: true });
    const isSuggestionsOpen = hasSearchResults && isInputFocused && !!searchValue && isComboboxOpen;
    const highlightedOption = isSuggestionsOpen && highlightedIndex >= 0 ? flatOptions[highlightedIndex] : undefined;
    const previewValue = isKeyboardHighlight ? getOptionLabel(highlightedOption) : '';
    const getOptionState = useCallback((option, quiet) => {
        if (highlightedIndex !== option.flatIndex) {
            return quiet ? 'highlighted' : 'enabled';
        }
        return isKeyboardHighlight ? 'focused' : quiet ? 'highlighted' : 'enabled';
    }, [highlightedIndex, isKeyboardHighlight]);
    const onButtonSearchClickHandler = () => {
        if (searchValue.length === 0) {
            inputElementSetFocus();
            setIsSearchMenuActive(true);
            return;
        }
        setIsSearchMenuActive(false);
        if (highlightedOption) {
            navigateToOption(highlightedOption);
        }
        else {
            navigateToGenericSearch();
        }
    };
    const onSubmitCatcher = (e) => {
        e.preventDefault();
    };
    const listBoxContent = (_jsxs("div", Object.assign({}, menuProps, { children: [(hasSuggestionResults || hasProductResults) && (_jsxs(Grid, { columns: 12, gap: 0, children: [hasSuggestionResults && (_jsxs(_Fragment, { children: [_jsx(Grid.Cell, { span: { xs: 12, lg: 5 }, paddingBlockStart: { lg: '6' }, paddingBlockEnd: PADDING, children: _jsx(ListBox, { testId: generateTestId({ testId: TEST_ID, suffix: 'block' }), children: _jsx(Box, { paddingInlineStart: PADDING, paddingInlineEnd: "5", children: _jsxs(Stack, { direction: "col", gap: "3", children: [_jsx(MenuSearchSectionTitle, { title: t('flyout.title.searchsuggestions'), testId: TEST_ID }), _jsxs(ListBox.Group, { testId: generateTestId({ testId: TEST_ID, suffix: 'group-suggestions' }), children: [keywordOptions.map((option, i) => (_jsx(MenuSearchResult, { testId: generateTestId({ testId: TEST_ID, suffix: `item-${i}` }), itemProps: getItemProps({ item: option, index: option.flatIndex }), state: getOptionState(option), url: option.data.url, children: _jsx(MenuSearchResultSuggestion, { result: option.data, searchValue: searchValue, type: MenuSearchResultBlockVariant.SUGGESTIONS }) }, option.optionId))), searchesBrandOrCatOptions.map((option, i) => (_jsx(MenuSearchResult, { testId: generateTestId({ testId: TEST_ID, suffix: `item-${i}` }), itemProps: getItemProps({ item: option, index: option.flatIndex }), state: getOptionState(option), url: option.data.url, children: _jsx(MenuSearchResultSuggestion, { result: option.data, searchValue: searchValue, type: MenuSearchResultBlockVariant.BRAND_OR_CAT }) }, option.optionId)))] })] }) }) }) }), !isDesktop && hasProductResults && (_jsx(Grid.Cell, { span: 12, children: _jsx(Divider, {}) }))] })), hasProductResults && (_jsx(Grid.Cell, { span: { xs: 12, lg: 7 }, paddingBlock: PADDING, children: _jsx(ListBox, { testId: generateTestId({ testId: TEST_ID, suffix: 'block' }), children: _jsx(Box, { paddingInlineStart: "5", paddingInlineEnd: PADDING, children: _jsxs(Stack, { direction: "col", gap: "3", children: [_jsx(MenuSearchSectionTitle, { title: t('flyout.title.productsuggestions'), testId: TEST_ID }), _jsx(ListBox.Group, { testId: generateTestId({ testId: TEST_ID, suffix: 'group-products' }), children: productOptions.map((option, i) => (_jsx(MenuSearchResult, { testId: generateTestId({ testId: TEST_ID, suffix: `item-${i}` }), itemProps: getItemProps({ item: option, index: option.flatIndex }), state: getOptionState(option), variant: "plain", children: _jsx(MenuSearchResultProduct, { product: option.data, tabIndex: -1 }) }, option.optionId))) })] }) }) }) })), !isDesktop && hasContentResults && (_jsx(Grid.Cell, { span: 12, children: _jsx(Divider, {}) }))] })), hasContentResults && (_jsx(Box, { backgroundColor: "quiet", children: _jsx(ListBox, { testId: generateTestId({ testId: TEST_ID, suffix: 'block' }), children: _jsx(Box, { padding: PADDING, children: _jsxs(Stack, { direction: "col", gap: "3", children: [_jsx(MenuSearchSectionTitle, { title: t('flyout.title.contentsuggestions'), testId: TEST_ID }), _jsx(ListBox.Group, { testId: generateTestId({ testId: TEST_ID, suffix: 'group-content' }), children: _jsx(Stack, { direction: "col", children: contentOptions.map((option, i) => (_jsxs(Fragment, { children: [_jsx(MenuSearchResult, { testId: generateTestId({ testId: TEST_ID, suffix: `item-${i}` }), itemProps: getItemProps({ item: option, index: option.flatIndex }), state: getOptionState(option, true), variant: "plain", children: _jsx(MenuSearchResultContent, { result: option.data, tabIndex: -1 }) }), !isLastItem(contentOptions, i) && _jsx(Divider, {}, `divider-${i}`)] }, option.optionId))) }) })] }) }) }) }))] })));
    const containerStyle = Object.assign({}, (!isDesktop && isInputFocused
        ? {
            position: 'fixed',
            inset: 0,
            zIndex: '550',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#fff',
        }
        : isDesktop && (isInputFocused || isExpandedLayoutActive)
            ? {
                zIndex: '550',
                width: containerWidth,
                height: '44px',
            }
            : {}));
    const headerStyle = Object.assign({ position: 'relative', transitionProperty: 'all', transitionDuration: '150ms', transitionTimingFunction: 'ease-in-out' }, (!isDesktop && isInputFocused
        ? {
            padding: '1.25rem',
            backgroundColor: '#fff',
            transitionProperty: 'background-color',
            transitionDuration: '100ms',
        }
        : {}));
    const animationStyle = isDesktop
        ? isInputFocused || isExpandedLayoutActive
            ? {
                position: 'absolute',
                width: isInputFocused ? POPOVER_WIDTH : containerWidth,
                transform: `translateX(${isInputFocused ? translateXValue : 0}px)`,
                transition: 'transform .2s ease-in-out, width .2s ease-in-out',
            }
            : {
                width: containerWidth,
                transition: 'transform .2s ease-in-out, width .2s ease-in-out',
            }
        : {};
    const handleClickBackdrop = () => {
        setIsInputFocused(false);
    };
    return (_jsxs("form", { method: "GET", action: listerPageUrl, onSubmit: onSubmitCatcher, suppressHydrationWarning: true, style: { display: 'grid' }, children: [_jsxs("div", { ref: containerRef, style: containerStyle, children: [_jsx("div", { style: headerStyle, children: _jsxs("div", { style: animationStyle, onTransitionEnd: () => {
                                if (!isInputFocused)
                                    setIsExpandedLayoutActive(false);
                            }, children: [isDesktop &&
                                    isInputFocused &&
                                    createPortal(_jsx(Backdrop, { onClick: handleClickBackdrop, zIndex: "overlay" }), document.body), _jsx(SearchFieldAlpha, { input: Object.assign(Object.assign({}, comboboxInputProps), { testId: 'search-input', name: 'q', placeholder: t('flyout.search.placeholder'), 'aria-expanded': isSuggestionsOpen, 'aria-controls': isSuggestionsOpen ? menuProps.id : undefined, 'aria-activedescendant': highlightedOption ? highlightedOption.optionId : undefined, onFocus: () => {
                                            setIsInputFocused(true);
                                            setIsExpandedLayoutActive(true);
                                            if (!isSearchMenuActive)
                                                setIsSearchMenuActive(true);
                                            if (searchValue) {
                                                openMenu();
                                            }
                                        }, adornmentStart: !isDesktop && isInputFocused ? (_jsx(Box, { paddingInlineEnd: "1", children: _jsx(IconButton, { variant: "plain", size: "sm", icon: {
                                                    source: ArrowLeftIcon,
                                                    color: 'neutral',
                                                }, onClick: onButtonBackClickHandler }) })) : undefined }), value: previewValue || searchValue, autoSearch: true, onChange: handleInputChange, onClear: onInputClearClickHandler, onSubmitAdornmentClick: onButtonSearchClickHandler }, "search-input")] }) }), !isDesktop && isInputFocused && _jsx(Box, { overflow: "auto", children: isSuggestionsOpen && listBoxContent })] }), isDesktop && (_jsx(Popover, { isOpen: isSuggestionsOpen, placement: "bottom-start", offsetOptions: { alignmentAxis: -40, mainAxis: 12 }, customWidth: `${POPOVER_WIDTH}px`, padding: "0", floatingClickProps: { enabled: false }, floatingDismissProps: { enabled: false }, focusManager: { disabled: true }, role: "listbox", targetElement: searchInputElementRef.current, children: _jsx(Popover.Body, { children: _jsx(_Fragment, { children: hasSearchResults ? listBoxContent : null }) }) }, "search-popover")), isShopLanding && _jsx(MenuSearchBarcodeReader, { onScan: onBarcodeScanHandler, minLength: 9 })] }));
};
